import React, { useEffect, useState } from "react";
import { useParams } from "wouter";
import {
  CheckCircle2,
  Loader2,
  MapPin,
  QrCode,
  Scissors,
  UsersRound,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { formatBrazilPhone, isValidBrazilPhone } from "@/lib/inputFormatters";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type CheckInResult =
  | { ok: true; alreadyCheckedIn: boolean; position: number }
  | { ok: false; reason: "not_found" };

function formatClientsAhead(position: number) {
  const clientsAhead = Math.max(0, position - 1);
  if (clientsAhead === 0) return "Você é a primeira pessoa da fila.";
  if (clientsAhead === 1) return "Há 1 pessoa à sua frente.";
  return `Há ${clientsAhead} pessoas à sua frente.`;
}

export default function ClientCheckInPage() {
  const { salonId: salonIdParam } = useParams<{ salonId: string }>();
  const salonId = Number(salonIdParam);
  const [phone, setPhone] = useState("");
  const [savedPhone, setSavedPhone] = useState("");
  const [result, setResult] = useState<CheckInResult | null>(null);
  const { data: salon } = trpc.salon.getPublicInfo.useQuery(
    { salonId } as any,
    { enabled: Number.isFinite(salonId) && salonId > 0 }
  );
  const checkIn = (trpc as any).queue.checkInByPhone.useMutation({
    onSuccess: (response: CheckInResult) => {
      if (response?.ok) {
        const formattedPhone = formatBrazilPhone(phone);
        window.localStorage.setItem(
          `beauty-checkin-phone-${salonId}`,
          formattedPhone
        );
        setSavedPhone(formattedPhone);
      }
      setResult(response);
    },
  });
  const salonName = (salon as any)?.name ?? "Beleza e Bronze";

  useEffect(() => {
    if (!salonId) return;
    const storedPhone =
      window.localStorage.getItem(`beauty-checkin-phone-${salonId}`) ?? "";
    setSavedPhone(storedPhone);
    if (storedPhone) setPhone(storedPhone);
  }, [salonId]);

  const submit = () => {
    const formatted = formatBrazilPhone(phone);
    if (!isValidBrazilPhone(formatted) || !salonId) return;
    checkIn.mutate({ salonId, phone: formatted });
  };

  const showSuccess = result?.ok === true;
  const wasAlreadyCheckedIn = showSuccess && result.alreadyCheckedIn;
  const position = showSuccess ? result.position : 0;

  return (
    <main className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-amber-950 px-4 py-8 text-white">
      <section className="mx-auto max-w-md space-y-5">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6 text-center shadow-2xl backdrop-blur">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-400/15 text-amber-300">
            <QrCode className="h-7 w-7" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-200/80">
            Check-in digital
          </p>
          <h1 className="mt-2 text-2xl font-bold">Cheguei ao {salonName}</h1>
          <p className="mt-2 text-sm leading-relaxed text-white/65">
            Informe o telefone usado no agendamento para entrar na fila. A
            equipe acompanha sua chegada pelo painel de chamada.
          </p>
        </div>

        {showSuccess ? (
          <div
            aria-live="polite"
            className="rounded-3xl border border-emerald-400/30 bg-emerald-400/10 p-6 text-center shadow-xl"
          >
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-300" />
            <h2 className="mt-3 text-xl font-bold">
              {wasAlreadyCheckedIn
                ? "Chegada já registrada"
                : "Chegada confirmada"}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-emerald-50">
              {wasAlreadyCheckedIn
                ? "Seu check-in para hoje já estava confirmado. Não criamos uma nova entrada na fila."
                : "Recebemos seu check-in para hoje e sua entrada na fila foi registrada."}
            </p>

            <div className="mt-5 rounded-2xl border border-emerald-300/25 bg-stone-950/25 p-5">
              <div className="flex items-center justify-center gap-2 text-emerald-100/80">
                <UsersRound className="h-4 w-4" />
                <span className="text-xs font-semibold uppercase tracking-[0.16em]">
                  Sua posição na fila
                </span>
              </div>
              <p className="mt-2 text-5xl font-black tabular-nums text-white">
                {position}ª
              </p>
              <p className="mt-3 text-sm font-medium text-emerald-50">
                {formatClientsAhead(position)}
              </p>
            </div>

            <p className="mt-5 text-xs leading-relaxed text-emerald-100/70">
              Fique à vontade. A equipe chamará você assim que estiver tudo
              pronto.
            </p>
          </div>
        ) : result?.reason === "not_found" ? (
          <div className="rounded-3xl border border-amber-400/30 bg-amber-400/10 p-6 text-center">
            <MapPin className="mx-auto h-10 w-10 text-amber-300" />
            <h2 className="mt-3 text-lg font-bold">
              Agendamento não localizado para hoje
            </h2>
            <p className="mt-2 text-sm text-white/70">
              Confira o telefone informado ou fale com a recepção. Nenhum
              check-in foi criado.
            </p>
            <Button
              variant="outline"
              className="mt-4 border-white/20 bg-white/5 text-white hover:bg-white/10"
              onClick={() => setResult(null)}
            >
              Tentar novamente
            </Button>
          </div>
        ) : (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
            <label className="text-sm font-medium">Seu telefone com DDD</label>
            {savedPhone && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 h-9 w-full border-amber-300/30 bg-amber-300/10 text-amber-100 hover:bg-amber-300/20"
                onClick={() => setPhone(savedPhone)}
              >
                Usar número salvo: {savedPhone}
              </Button>
            )}
            <Input
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={event =>
                setPhone(formatBrazilPhone(event.target.value))
              }
              onKeyDown={event => {
                if (event.key === "Enter") submit();
              }}
              placeholder="(11) 99999-9999"
              className="mt-3 h-14 border-white/15 bg-white/10 text-lg text-white placeholder:text-white/35"
            />
            <Button
              onClick={submit}
              disabled={!isValidBrazilPhone(phone) || checkIn.isPending}
              className="mt-4 h-14 w-full bg-amber-500 text-base font-semibold text-stone-950 hover:bg-amber-400"
            >
              {checkIn.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Confirmando...
                </>
              ) : (
                <>
                  <Scissors className="mr-2 h-4 w-4" />
                  Confirmar minha chegada
                </>
              )}
            </Button>
            <p className="mt-3 text-center text-xs text-white/45">
              O número fica salvo somente neste aparelho para agilizar seu
              próximo check-in.
            </p>
          </div>
        )}
      </section>
    </main>
  );
}
